/**
 * Filters undefined/null values from an array
 * @example
 * const array: (number | undefined)[] = [1, 2, undefined, 3];
 * const withoutUndefineds = filterNonValuesFromArray(array);
 * // result: [1, 2, 3]
 * @example
 * const array: [] | undefined | null = undefined;
 * const withoutUndefineds = filterNonValuesFromArray(array);
 * // result: []
 */
export const filterNonValuesFromArray = <T>(
  array: (T | undefined | null)[] | null | undefined
): T[] => {
  if (array === null || array === undefined) {
    return [];
  }

  return array.reduce((acc: T[], val: T | undefined | null) => {
    if (val !== undefined && val !== null) {
      acc.push(val);
    }

    return acc;
  }, []);
};

/**
 * Applies a selection change to an ordered list, preserving existing order.
 * Items in `oldSelected` but not in `newSelected` are removed.
 * Items in `newSelected` but not in `oldSelected` are appended.
 */
export function applySelectionChange<T>(
  items: T[],
  oldSelected: T[],
  newSelected: T[]
): T[] {
  const newSet = new Set(newSelected);
  const oldSet = new Set(oldSelected);
  const added = newSelected.filter((v) => !oldSet.has(v));
  const removed = new Set(oldSelected.filter((v) => !newSet.has(v)));

  return [...items.filter((item) => !removed.has(item)), ...added];
}

export function updateItemAt<T>(list: T[], index: number, next: T): T[] {
  return list.map((item, position) => (position === index ? next : item));
}

export function removeItemAt<T>(list: T[], index: number): T[] {
  return list.filter((_, position) => position !== index);
}

/**
 * Moves an item from one index to another, returning a new array.
 */
export function applyMoveChange<T>(
  items: T[],
  oldIndex: number,
  targetIndex: number
): T[] {
  const newOrder = [...items];
  const [moved] = newOrder.splice(oldIndex, 1);
  newOrder.splice(targetIndex, 0, moved);

  return newOrder;
}

export function normalizeStringList(values: string[]): string[] {
  const seen = new Set<string>();

  return values.reduce<string[]>((accumulated, value) => {
    const trimmed = value.trim();
    if (trimmed === '' || seen.has(trimmed)) {
      return accumulated;
    }
    seen.add(trimmed);

    return [...accumulated, trimmed];
  }, []);
}
